<template>
  <SiteNav />
  <router-view @openBrief="openCalculatedBrief" @openTemplate="openTemplateBrief" />
  <SiteFooter />
  <BriefModal
    :open="briefModalOpen"
    :briefData="activeBriefData"
    @close="closeBriefModal"
  />
  <CookieBanner />
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import SiteNav from './components/SiteNav.vue'
import SiteFooter from './components/SiteFooter.vue'
import BriefModal from './components/BriefModal.vue'
import CookieBanner from './components/CookieBanner.vue'
import { useBriefGenerator } from './composables/useBriefGenerator'
import { useScrollReveal } from './composables/useScrollReveal'
import { useLocale } from './composables/useLocale'

const { getTemplateData, buildBriefData } = useBriefGenerator()
const { t } = useLocale()
useScrollReveal()

const route = useRoute()

watch(() => route.path, () => {
  const isEn = route.path.startsWith('/en')
  const base = 'https://www.minderrente.nl'
  const canonical = isEn ? `${base}/en/` : `${base}/`

  document.title = t('meta.title')

  const metaUpdates = {
    'meta[name="description"]': ['content', t('meta.description')],
    'meta[property="og:title"]': ['content', t('meta.ogTitle')],
    'meta[property="og:description"]': ['content', t('meta.ogDescription')],
    'meta[property="og:url"]': ['content', canonical],
    'meta[property="og:locale"]': ['content', isEn ? 'en_GB' : 'nl_NL'],
    'meta[name="twitter:title"]': ['content', t('meta.ogTitle')],
    'meta[name="twitter:description"]': ['content', t('meta.ogDescription')],
    'meta[name="twitter:url"]': ['content', canonical],
  }

  for (const [selector, [attr, value]] of Object.entries(metaUpdates)) {
    const el = document.querySelector(selector)
    if (el) el.setAttribute(attr, value)
  }

  document.documentElement.lang = isEn ? 'en' : 'nl'
}, { immediate: true })

const briefModalOpen = ref(false)
const activeBriefData = ref(null)

function openCalculatedBrief({ briefNummer, data }) {
  const briefData = buildBriefData(briefNummer, data)
  if (!briefData) return
  activeBriefData.value = briefData
  briefModalOpen.value = true
}

function openTemplateBrief(templateKey) {
  const data = getTemplateData(templateKey)
  if (!data) return
  activeBriefData.value = data
  briefModalOpen.value = true
}

function closeBriefModal() {
  briefModalOpen.value = false
}
</script>

<style>
#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
#app > footer {
  margin-top: auto;
}
body.modal-open { overflow: hidden; }
</style>
